import React, { FC, Fragment, useState } from "react";
import { ExternalChatOrganization, ExternalChatUserState, } from "domain-entities";
import OrganizationSuggestionCtaView from "Components/OrganizationSuggestion/OrganizationSuggestionCtaView";
import { Box, List, ListItem, makeStyles, OutlinedInput } from "@material-ui/core";
import { getColorRgbaFromHex } from "Helpers/ColorHelper";
import { color, px } from "Configs/theme";
import FadeInBox from "Components/Layout/FadeInBox";
import ErrorView from "Components/Layout/ErrorView";
import { useTranslation } from "Providers/TranslationProvider";
import OrganizationAvatar from "./OrganizationAvatar";
import OrganizationSignInModal from "./OrganizationSignInModal";
import { useModalController } from "Components/Modal/Modal";
import { SaveDocumentStore } from "Helpers/StoreHelper";
import { AddCircle } from "@material-ui/icons";

interface Props {
  organizations: ExternalChatOrganization[];
  viewerUserStateStore: SaveDocumentStore<ExternalChatUserState>;
  openWithOrganization?: ExternalChatOrganization;
}

const OrganizationsAddableView: FC<Props> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const organizationSignInMc = useModalController<ExternalChatOrganization>();
  const [organizationInitOpened, setOrganizationInitOpened] = useState(false);

  if (props.openWithOrganization && !organizationInitOpened) {
    organizationSignInMc.open(props.openWithOrganization);
    setOrganizationInitOpened(true);
  }

  function renderOrganizationListItem(organization: ExternalChatOrganization) {
    return (
      <ListItem
        className={classes.listItem}
        onClick={() => organizationSignInMc.open(organization)}
        button
      >
        <OrganizationAvatar organization={organization}/>
        <Box className={classes.content}>
          <Box className={classes.name}>{organization.name}</Box>
          {organization.location && (
            <Box className={classes.location}>{organization.location.name}</Box>
          )}
        </Box>
        <AddCircle color="primary" fontSize="small"/>
      </ListItem>
    );
  }

  function renderOrganizationList() {
    let filteredOrganizations = props.organizations.filter(
      (organization) => organization.state === "active"
    );

    if (searchText) {
      filteredOrganizations = filteredOrganizations.filter(
        (organization) =>
          organization.name
            .toLocaleLowerCase()
            .indexOf(searchText.toLocaleLowerCase()) > -1
      );
    }

    if (filteredOrganizations.length) {
      return (
        <List className={classes.list}>
          {filteredOrganizations.map((organization, index) => (
            <FadeInBox key={organization.id} delayIndex={index} delay={70}>
              {renderOrganizationListItem(organization)}
            </FadeInBox>
          ))}
        </List>
      );
    } else {
      return (
        <ErrorView
          severity="info"
          color="warning"
          padding={px.none}
          error={new Error(t("OrganizationsAddableView_Error_NoOrganizations"))}
        />
      );
    }
  }

  return (
    <Fragment>
      <Box className={classes.scroll}>
        <Box className={classes.searchBox}>
          <OutlinedInput
            classes={{
              root: classes.searchTextField,
              notchedOutline: classes.searchTextFieldNotchedOutline
            }}
            margin="dense"
            onChange={(event) => setSearchText(event.currentTarget.value)}
            placeholder={t("OrganizationsAddableView_Label_Search")}
            fullWidth
          />
        </Box>
        {renderOrganizationList()}
      </Box>
      <OrganizationSuggestionCtaView/>
      <OrganizationSignInModal
        modalController={organizationSignInMc}
        viewerUserStateStore={props.viewerUserStateStore}
      />
    </Fragment>
  );
};

export default OrganizationsAddableView;

const useStyles = makeStyles({
  scroll: {
    height: 350,
    padding: px.m,
    overflowY: "scroll",
    border: `1px solid ${getColorRgbaFromHex(color.text, 0.15)}`,
    borderRadius: px.xs,
    backgroundColor: getColorRgbaFromHex(color.text, 0.1),
    marginBottom: px.xl,
  },
  searchBox: {
    backgroundColor: color.card,
    marginBottom: px.xl,
    borderRadius: px.xs
  },
  searchTextField: {
    backgroundColor: getColorRgbaFromHex(color.grayBackground00, 0.50),
  },
  searchTextFieldNotchedOutline: {
    borderWidth: "0 !important",
  },
  list: {
    padding: px.none,
  },
  listItem: {
    display: "flex",
    backgroundColor: color.card,
    borderRadius: px.xs,
    marginBottom: px.s,
    padding: px.m,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    paddingLeft: px.m,
    width: 0,
  },
  name: {
    fontSize: "14px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginBottom: px.xs,
  },
  location: {
    fontSize: "12px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    opacity: 0.5,
  },
});
